import React, { useMemo, useEffect } from "react";
// COMPONENTS
import { performancePages } from "../../../../constants";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { RootStateOrAny, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
// queries
import { getBrandMarketShareTrend, getBrandMarketShareTable } from "../../../../api/brandPerformanceApi";
import Ratings from "../../../../components/Ratings/NewRatings";
import BrandPerformanceTableTile from "../BrandPerformanceTableTile";
import BrandPerformanceTrendTile from "../BrandPerformanceTrendTile";
import { isNullOrUndefined } from "../../../../utils/utilityFunctions";

interface Props extends RouteComponentProps {
  activePage: performancePages;
  diff: number;
  onHandleRadioClick: any;
  nissanCompetitor: string;
  granularity: string;
}

const BrandMarketContent = withRouter((props: Props) => {
  const { activePage, diff, history, onHandleRadioClick, nissanCompetitor, granularity } = props;

  const params = useSelector((state: RootStateOrAny) => state.parameters);

  // fetch the trend and average data
  const {
    data: marketShareTrendData,
    isFetching: loadingMarketShareTrendData,
    refetch: refetchMarketShareTrendData,
  } = useQuery(["marketShareTrendData", granularity, history.location.search], () => getBrandMarketShareTrend({ ...params, nissan_competitor: nissanCompetitor }), {
    enabled: false,
  });

  const {
    data: marketShareTableData,
    isFetching: loadingMarketShareTableData,
    refetch: refetchMarketShareTableData,
  } = useQuery(["marketShareTableData", granularity, history.location.search], () => getBrandMarketShareTable({ ...params, nissan_competitor: nissanCompetitor }), {
    enabled: false,
  });

  // when the url changes, refetch the data
  useEffect(() => {
    refetchMarketShareTableData();
    refetchMarketShareTrendData();
  }, [history.location.search]);

  const columns = [
    {
      id: "model",
      Header: `Brand `,
      accessor: "model",
      defaultCanSort: true,
      sortDescFirst: true,
      sortType: "alphanumericIgnoreCase",
      Cell: (props1: { value: string }) => {
        const { value } = props1;

        return value;
      },
    },
    {
      Header: "MS",
      accessor: `market_share`,
      id: "market_share",
      defaultCanSort: true,
      defaultSortDesc: true,
      sortDescFirst: true,
      sortType: "alphanumericCustom",
      Cell: (props: { cell: { row: { original: any } } }) => {
        const {
          cell: {
            row: { original },
          },
        } = props;
        const value = original?.["market_share"];
        return !isNullOrUndefined(value) ? `${value}%` : "n/a";
      },
    },
    {
      Header: "YoY",
      accessor: `market_share_yoy`,
      id: "market_share_yoy",
      defaultCanSort: true,
      defaultSortDesc: true,
      sortDescFirst: true,
      sortType: "basicCustom",
      Cell: (props: { cell: { row: { original: any } } }) => {
        const {
          cell: {
            row: { original },
          },
        } = props;
        const value = original?.["market_share_yoy"];
        return (
          <div className="sub-text">
            <span>{!isNullOrUndefined(value) ? <Ratings value={value} /> : null}</span>
            <span>{!isNullOrUndefined(value) ? `${value}%` : "n/a"} </span>{" "}
          </div>
        );
        // return value ? `${value}%` : "n/a";
      },
    },
  ];

  const dateRange = marketShareTableData?.date_range ?? [];

  const dateTitle = useMemo(() => {
    if (dateRange && dateRange.length > 1) {
      return `(${dateRange[0]} - ${dateRange.slice(-1)})`;
    }

    return "";
  }, [dateRange]);

  return (
    <div className="brand-market-main-content">
      <BrandPerformanceTrendTile
        title="Market Share Trend"
        chartId="marketShareTrend"
        chartData={marketShareTrendData}
        loadingTrend={loadingMarketShareTrendData}
        diff={diff}
        nissanCompetitor={nissanCompetitor}
        onHandleRadioClick={onHandleRadioClick}
        isPercentage={true}
        chartGranularity={granularity}
      />
      <BrandPerformanceTableTile
        title="Market Share Average"
        tableId="brand_market_share_table"
        tableData={marketShareTableData}
        columns={columns}
        loadingTable={loadingMarketShareTableData}
        initialSortCol={{ id: "market_share", desc: true }}
      />
    </div>
  );
});

export default BrandMarketContent;
